import React from 'react';
import { Helmet } from 'react-helmet-async';
import { DownloadIcon, KeyIcon, LogInIcon, HomeIcon, CalendarIcon, CheckCircleIcon, LineChartIcon, BellIcon, CreditCardIcon, BookOpenIcon, PencilIcon, VideoIcon, AppleIcon, SmartphoneIcon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const TotalDrivePage = () => {
  return <div className="bg-white" data-id="element-2049">
      <Helmet data-id="element-2050">
        <title data-id="element-2051">Total Drive App Guide | Northampton Driving School</title>
        <meta name="description" content="Learn how to use the Total Drive app for booking driving lessons in Northampton, tracking progress, making payments, and more with our step-by-step guide." data-id="element-2052" />
        <meta name="keywords" content="Total Drive app, driving lesson app, Northampton driving lessons, book driving lessons online, driving lesson progress tracker, driving lesson payment app" data-id="element-2053" />
      </Helmet>
      <div className="container mx-auto px-4 py-12" data-id="element-2054">
        <h1 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2055">
          Total Drive App Guide
        </h1>
        {/* Intro Section */}
        <section className="mb-12 bg-gray-50 p-6 rounded-lg border border-gray-200" data-id="element-2056">
          <h2 className="text-2xl font-bold mb-4" data-id="element-2057">
            Simplify Your Learning Journey
          </h2>
          <p className="text-gray-700 mb-4" data-id="element-2058">
            At Northampton Driving School, we use the Total Drive app to make
            learning simple and stress-free. With the app, you can:
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-id="element-2059">
            <li className="flex items-start" data-id="element-2060">
              <CalendarIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2061" />
              <span data-id="element-2062">Book your driving lessons in Northampton</span>
            </li>
            <li className="flex items-start" data-id="element-2063">
              <LineChartIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2064" />
              <span data-id="element-2065">Track your progress with your instructor</span>
            </li>
            <li className="flex items-start" data-id="element-2066">
              <CreditCardIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2067" />
              <span data-id="element-2068">Pay for lessons securely</span>
            </li>
            <li className="flex items-start" data-id="element-2069">
              <BellIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2070" />
              <span data-id="element-2071">Receive notifications and reminders</span>
            </li>
          </ul>
          <p className="text-gray-700" data-id="element-2072">
            Follow this step-by-step guide with screenshots to get started.
          </p>
        </section>
        {/* Step-by-Step Guide */}
        <div className="space-y-12" data-id="element-2073">
          {/* Step 1 */}
          <section id="step1" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2074">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2075">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2076">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2077">
                  1
                </span>
                Download the Total Drive App
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2078">
              <div data-id="element-2079">
                <p className="mb-4" data-id="element-2080">Download from:</p>
                <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-2081">
                  <a href="https://apps.apple.com/gb/app/total-drive/id1446269375" target="_blank" rel="noopener noreferrer" className="flex items-center" data-id="element-2082">
                    <img src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg" alt="Download on the App Store" className="h-12" data-id="element-2083" />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.totaldrive.com.totaldrive&hl=en_GB" target="_blank" rel="noopener noreferrer" className="flex items-center" data-id="element-2084">
                    <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" className="h-14" data-id="element-2085" />
                  </a>
                </div>
                <p className="mb-4" data-id="element-2086">
                  <span className="font-medium" data-id="element-2087">1.</span> Open the app and
                  select <strong data-id="element-2088">Pupil / Parent</strong>.
                </p>
              </div>
              <div className="flex justify-center" data-id="element-2089">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fchFF5RWyasy6zz1WoRWeZ/01._Step_1.jpg" alt="Total Drive App Welcome Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2090" />
              </div>
            </div>
          </section>
          {/* Step 2 */}
          <section id="step2" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2091">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2092">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2093">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2094">
                  2
                </span>
                Enter Your Driving School PIN
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2095">
              <div data-id="element-2096">
                <p className="mb-4" data-id="element-2097">
                  <span className="font-medium" data-id="element-2098">1.</span> Enter the 4-digit PIN
                  sent by your instructor.
                </p>
                <p className="mb-4" data-id="element-2099">
                  <span className="font-medium" data-id="element-2100">2.</span> Tap{' '}
                  <strong data-id="element-2101">Continue</strong>.
                </p>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6" data-id="element-2102">
                  <p className="text-sm text-yellow-800" data-id="element-2103">
                    <strong data-id="element-2104">Tip:</strong> Don't have a PIN? Contact your
                    Northampton Driving School instructor to get your unique
                    code.
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2105">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8kd2oG1RPMEfFy7oPFtUzC/01._Step_2.jpg" alt="Total Drive App PIN Entry Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2106" />
              </div>
            </div>
          </section>
          {/* Step 3 */}
          <section id="step3" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2107">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2108">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2109">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2110">
                  3
                </span>
                Log In to Your Account
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2111">
              <div data-id="element-2112">
                <p className="mb-4" data-id="element-2113">
                  <span className="font-medium" data-id="element-2114">1.</span> Enter your mobile
                  number and password (provided via text).
                </p>
                <p className="mb-4" data-id="element-2115">
                  <span className="font-medium" data-id="element-2116">2.</span> Tap{' '}
                  <strong data-id="element-2117">Login</strong> and accept the terms and conditions.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6" data-id="element-2118">
                  <p className="text-sm text-blue-800" data-id="element-2119">
                    <strong data-id="element-2120">Note:</strong> If you haven't received your login
                    details, please contact your instructor.
                  </p>
                </div>
              </div>
              <div className="flex justify-center gap-4" data-id="element-2121">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/87gBv1txfEfPqrMaoh2FjX/03._Step_3.jpg" alt="Total Drive App Login Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2122" />
              </div>
            </div>
          </section>
          {/* Step 4 */}
          <section id="step4" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2123">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2124">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2125">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2126">
                  4
                </span>
                Explore the Home Screen
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2127">
              <div data-id="element-2128">
                <p className="mb-4" data-id="element-2129">On the home screen, you can:</p>
                <ul className="space-y-3 mb-6" data-id="element-2130">
                  <li className="flex items-start" data-id="element-2131">
                    <CalendarIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2132" />
                    <span data-id="element-2133">View your next driving lesson at a glance</span>
                  </li>
                  <li className="flex items-start" data-id="element-2134">
                    <CreditCardIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2135" />
                    <span data-id="element-2136">Pay for lessons</span>
                  </li>
                  <li className="flex items-start" data-id="element-2137">
                    <BookOpenIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2138" />
                    <span data-id="element-2139">Access learning resources</span>
                  </li>
                  <li className="flex items-start" data-id="element-2140">
                    <CalendarIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2141" />
                    <span data-id="element-2142">Check available lesson gaps</span>
                  </li>
                </ul>
              </div>
              <div className="flex justify-center" data-id="element-2143">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sZg5qHV1hWY2oqPQG9pxnu/04._Student_Home_Screen.jpg" alt="Total Drive App Home Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2144" />
              </div>
            </div>
          </section>
          {/* Step 5 */}
          <section id="step5" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2145">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2146">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2147">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2148">
                  5
                </span>
                Manage Your Driving Lessons
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2149">
              <div data-id="element-2150">
                <p className="mb-4" data-id="element-2151">
                  <span className="font-medium" data-id="element-2152">1.</span> Tap the{' '}
                  <strong data-id="element-2153">Lessons</strong> icon.
                </p>
                <p className="mb-4" data-id="element-2154">
                  <span className="font-medium" data-id="element-2155">2.</span> Switch between Future
                  Lessons and Past Lessons.
                </p>
                <p className="mb-4" data-id="element-2156">
                  <span className="font-medium" data-id="element-2157">3.</span> Tap the arrow to view
                  lesson details (pick-up/drop-off).
                </p>
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-6" data-id="element-2158">
                  <p className="text-sm text-green-800" data-id="element-2159">
                    <strong data-id="element-2160">Tip:</strong> You can see your total delivered,
                    cancelled, and booked hours at the top of the screen.
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2161">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sEaLNL3tXsQjznQWP5tTN1/05._Lessons_2.jpg" alt="Total Drive App Lessons Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2162" />
              </div>
            </div>
          </section>
          {/* Step 6 */}
          <section id="step6" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2163">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2164">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2165">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2166">
                  6
                </span>
                Check In for Your Lessons
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2167">
              <div data-id="element-2168">
                <p className="mb-4" data-id="element-2169">
                  <span className="font-medium" data-id="element-2170">1.</span> On your home screen,
                  you'll see your upcoming lessons.
                </p>
                <p className="mb-4" data-id="element-2171">
                  <span className="font-medium" data-id="element-2172">2.</span> Tap the{' '}
                  <strong data-id="element-2173">Check In</strong> button when you're ready for your
                  lesson.
                </p>
                <p className="mb-4" data-id="element-2174">
                  <span className="font-medium" data-id="element-2175">3.</span> You'll receive a
                  confirmation message when you've successfully checked in.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6" data-id="element-2176">
                  <p className="text-sm text-blue-800" data-id="element-2177">
                    <strong data-id="element-2178">Note:</strong> Checking in lets your instructor know
                    you're ready for your lesson, helping them stay on schedule.
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-4" data-id="element-2179">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2ghQFvuhW9ss5gBLwk5yKY/06._Check_In.jpg" alt="Total Drive App Check In Button" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2180" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fnUA3X8DKd2JrLjQz8yd9c/06._Check_In_2.jpg" alt="Total Drive App Check In Confirmation" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2181" />
              </div>
            </div>
          </section>
          {/* Step 7 */}
          <section id="step7" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2182">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2183">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2184">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2185">
                  7
                </span>
                Track Your Driving Progress
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2186">
              <div data-id="element-2187">
                <p className="mb-4" data-id="element-2188">
                  <span className="font-medium" data-id="element-2189">1.</span> Tap the{' '}
                  <strong data-id="element-2190">Favorites</strong> icon (star) in the bottom
                  navigation.
                </p>
                <p className="mb-4" data-id="element-2191">
                  <span className="font-medium" data-id="element-2192">2.</span> View your syllabus
                  completion percentage and skill levels.
                </p>
                <p className="mb-4" data-id="element-2193">
                  <span className="font-medium" data-id="element-2194">3.</span> Track your progress on
                  specific skills like cockpit checks, moving off, and more.
                </p>
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-6" data-id="element-2195">
                  <p className="text-sm text-green-800" data-id="element-2196">
                    <strong data-id="element-2197">Tip:</strong> Your instructor updates these ratings
                    after each lesson, so you can see how you're improving over
                    time.
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2198">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/i7D9GtBUrRTZPqYHsd9gmg/07._Progress_Performance.jpg" alt="Total Drive App Progress Tracking" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2199" />
              </div>
            </div>
          </section>
          {/* Step 8 */}
          <section id="step8" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2200">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2201">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2202">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2203">
                  8
                </span>
                Check Notifications & Messages
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2204">
              <div data-id="element-2205">
                <p className="mb-4" data-id="element-2206">
                  <span className="font-medium" data-id="element-2207">1.</span> Tap the{' '}
                  <strong data-id="element-2208">Bell</strong> icon in the bottom navigation to access
                  your inbox.
                </p>
                <p className="mb-4" data-id="element-2209">
                  <span className="font-medium" data-id="element-2210">2.</span> Here you'll find
                  important notifications about:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2211">
                  <li className="list-disc" data-id="element-2212">
                    Available lesson gaps you can book
                  </li>
                  <li className="list-disc" data-id="element-2213">
                    New lessons that have been added
                  </li>
                  <li className="list-disc" data-id="element-2214">
                    Updates to your progress syllabus
                  </li>
                  <li className="list-disc" data-id="element-2215">Messages from your instructor</li>
                </ul>
                <p className="mb-4" data-id="element-2216">
                  <span className="font-medium" data-id="element-2217">3.</span> You can also send
                  messages to your instructor using the text box at the bottom.
                </p>
              </div>
              <div className="flex justify-center" data-id="element-2218">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rnZfQJfhv81y4fgQwsmTD8/08._Notifications.jpg" alt="Total Drive App Notifications" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2219" />
              </div>
            </div>
          </section>
          {/* Step 9 */}
          <section id="step9" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2220">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2221">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2222">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2223">
                  9
                </span>
                Make Payments for Lessons
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2224">
              <div data-id="element-2225">
                <p className="mb-4" data-id="element-2226">
                  <span className="font-medium" data-id="element-2227">1.</span> Tap the{' '}
                  <strong data-id="element-2228">Card</strong> icon in the bottom navigation.
                </p>
                <p className="mb-4" data-id="element-2229">
                  <span className="font-medium" data-id="element-2230">2.</span> View your payment
                  history, including:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2231">
                  <li className="list-disc" data-id="element-2232">Hours purchased</li>
                  <li className="list-disc" data-id="element-2233">Hours delivered</li>
                  <li className="list-disc" data-id="element-2234">Credit remaining</li>
                </ul>
                <p className="mb-4" data-id="element-2235">
                  <span className="font-medium" data-id="element-2236">3.</span> To make a payment, tap
                  the <strong data-id="element-2237">Pay</strong> button.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6" data-id="element-2238">
                  <p className="text-sm text-blue-800" data-id="element-2239">
                    <strong data-id="element-2240">Note:</strong> All payments are secure and you'll
                    receive a confirmation email after each transaction.
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2241">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wiLiGzKARsbXPcg4NCrhoz/8._Payments.jpg" alt="Total Drive App Payments Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2242" />
              </div>
            </div>
          </section>
          {/* Step 10 */}
          <section id="step10" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2243">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2244">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2245">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2246">
                  10
                </span>
                Access Learning Resources
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2247">
              <div data-id="element-2248">
                <p className="mb-4" data-id="element-2249">
                  <span className="font-medium" data-id="element-2250">1.</span> From the home screen,
                  tap the <strong data-id="element-2251">Resources</strong> button.
                </p>
                <p className="mb-4" data-id="element-2252">
                  <span className="font-medium" data-id="element-2253">2.</span> Access helpful tools
                  such as:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2254">
                  <li className="list-disc" data-id="element-2255">Book Theory Test</li>
                  <li className="list-disc" data-id="element-2256">Book Driving Test</li>
                  <li className="list-disc" data-id="element-2257">MOT Check for vehicles</li>
                </ul>
                <p className="mb-4" data-id="element-2258">
                  <span className="font-medium" data-id="element-2259">3.</span> These resources link
                  directly to official DVSA services to make booking easy.
                </p>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6" data-id="element-2260">
                  <p className="text-sm text-yellow-800" data-id="element-2261">
                    <strong data-id="element-2262">Tip:</strong> Your instructor can advise when you're
                    ready to book your theory or practical test.
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-4" data-id="element-2263">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/w62cYShYUxT7SixVzRXVz9/9._Resources.jpg" alt="Total Drive App Resources Button" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2264" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tPSzB8qgWvdzoFXtkjkTHf/9._Resources_2.jpg" alt="Total Drive App Resources Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2265" />
              </div>
            </div>
          </section>
          {/* Step 11 */}
          <section id="step11" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2266">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2267">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2268">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2269">
                  11
                </span>
                Practice for Your Theory Test
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2270">
              <div data-id="element-2271">
                <p className="mb-4" data-id="element-2272">
                  <span className="font-medium" data-id="element-2273">1.</span> From the home screen,
                  tap <strong data-id="element-2274">Theory Practice</strong>.
                </p>
                <p className="mb-4" data-id="element-2275">
                  <span className="font-medium" data-id="element-2276">2.</span> You can purchase
                  access to the Total Drive Theory Suite for just £4.99.
                </p>
                <p className="mb-4" data-id="element-2277">
                  <span className="font-medium" data-id="element-2278">3.</span> The Theory Suite
                  includes:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2279">
                  <li className="list-disc" data-id="element-2280">
                    Official DVSA Theory Test questions
                  </li>
                  <li className="list-disc" data-id="element-2281">Hazard Perception videos</li>
                  <li className="list-disc" data-id="element-2282">Practice by subject</li>
                  <li className="list-disc" data-id="element-2283">Real-time mock tests</li>
                </ul>
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-6" data-id="element-2284">
                  <p className="text-sm text-green-800" data-id="element-2285">
                    <strong data-id="element-2286">Tip:</strong> Regular theory practice is essential
                    for passing your test the first time. We recommend 20
                    minutes of practice daily.
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-4" data-id="element-2287">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tj6ahZ1CWpcgrBTcoxp7aA/10._Theory_Practice_2.jpg" alt="Total Drive App Theory Suite Payment" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2288" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vuMREizzouevNyrxcivgNb/10._Theory_Practice.jpg" alt="Total Drive App Theory Practice Features" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2289" />
              </div>
            </div>
          </section>
          {/* Step 12 */}
          <section id="step12" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2290">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2291">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2292">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2293">
                  12
                </span>
                Use Reflective Logs for Learning
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2294">
              <div data-id="element-2295">
                <p className="mb-4" data-id="element-2296">
                  <span className="font-medium" data-id="element-2297">1.</span> From the home screen,
                  tap <strong data-id="element-2298">Reflective Logs</strong>.
                </p>
                <p className="mb-4" data-id="element-2299">
                  <span className="font-medium" data-id="element-2300">2.</span> After each lesson,
                  create a new log entry to record:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2301">
                  <li className="list-disc" data-id="element-2302">
                    What you achieved during the lesson
                  </li>
                  <li className="list-disc" data-id="element-2303">Skills you've developed</li>
                  <li className="list-disc" data-id="element-2304">Areas you want to improve</li>
                  <li className="list-disc" data-id="element-2305">
                    Action plans for your next session
                  </li>
                </ul>
                <p className="mb-4" data-id="element-2306">
                  <span className="font-medium" data-id="element-2307">3.</span> View past logs to
                  track your progress over time.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6" data-id="element-2308">
                  <p className="text-sm text-blue-800" data-id="element-2309">
                    <strong data-id="element-2310">Note:</strong> Reflective logs help you identify
                    patterns in your learning and accelerate your progress
                    toward test readiness.
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2311">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/uyqDSHaS4oyuff1pzA7UmY/11._Refelctive_Log.jpg" alt="Total Drive App Reflective Logs" className="max-w-full rounded-lg shadow-md mx-auto" data-id="element-2312" />
              </div>
            </div>
          </section>
          {/* Step 13 */}
          <section id="step13" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2313">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2314">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2315">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2316">
                  13
                </span>
                Access Premium Learning Aids{' '}
                <span className="ml-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full" data-id="element-2317">
                  FREE
                </span>
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2318">
              <div data-id="element-2319">
                <p className="mb-4" data-id="element-2320">
                  <span className="font-medium" data-id="element-2321">1.</span> From the home screen,
                  tap <strong data-id="element-2322">Learning Aids</strong>.
                </p>
                <p className="mb-4" data-id="element-2323">
                  <span className="font-medium" data-id="element-2324">2.</span> Explore premium
                  teaching materials that include:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2325">
                  <li className="list-disc" data-id="element-2326">Digital Driving Course</li>
                  <li className="list-disc" data-id="element-2327">36 Training Material Videos</li>
                  <li className="list-disc" data-id="element-2328">Junctions, Manoeuvres & more</li>
                  <li className="list-disc" data-id="element-2329">Voiceover Audio Option</li>
                </ul>
                <p className="mb-4" data-id="element-2330">
                  <span className="font-medium" data-id="element-2331">3.</span> These 3D video courses
                  provide visual learning to complement your practical lessons.
                </p>
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-6" data-id="element-2332">
                  <p className="text-sm text-green-800" data-id="element-2333">
                    <strong data-id="element-2334">Exclusive Benefit:</strong> While Learning Aids is
                    normally a premium paid service, Northampton Driving School
                    offers this valuable resource{' '}
                    <strong data-id="element-2335">completely FREE</strong> to all our students!
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2336">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ef7AdFrgTv9UA6N7T195GW/12._Learning_Aids_FREE.jpg" alt="Total Drive App Learning Aids showing 3D video courses" className="max-w-[300px] rounded-lg shadow-md mx-auto" data-id="element-2337" />
              </div>
            </div>
          </section>
          {/* Closing Section */}
          <section className="bg-burgundy-50 border border-burgundy-200 rounded-lg p-8 text-center" data-id="element-2338">
            <h2 className="text-2xl font-bold mb-4" data-id="element-2339">
              Take Control of Your Driving Journey
            </h2>
            <p className="mb-6 max-w-2xl mx-auto" data-id="element-2340">
              With the Total Drive app, you're always in control of your
              learning. From booking lessons to tracking your driving ability,
              everything is available in one place.
            </p>
            <p className="mb-8 font-medium" data-id="element-2341">
              Ready to begin? Book your driving lessons in Northampton today and
              start using the Total Drive app with Northampton Driving School.
            </p>
            <a href="https://www.totaldrive.app/a/enquiry_form.php?17584722727972" target="_blank" rel="noopener noreferrer" className="bg-burgundy-600 hover:bg-burgundy-700 text-white px-8 py-3 rounded-md font-medium inline-flex items-center transition-colors" data-id="element-2342">
              Book Your First Lesson
              <ArrowRightIcon size={16} className="ml-2" data-id="element-2343" />
            </a>
          </section>
        </div>
      </div>
    </div>;
};
export default TotalDrivePage;